(() => {
  const GLOBAL_KEY = '__cortailorPdpKeyElementsV3';
  const desktopQuery = window.matchMedia('(min-width: 992px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].refresh?.();
    return;
  }

  const registeredSections = new Map();
  let refreshRaf = 0;

  const getProductInfo = () => document.querySelector(
    '.main-product__content > .xo-product-info-content, .main-product__native-info-track > .xo-product-info-content'
  );

  const getStorySection = (wrapper) => wrapper?.querySelector('.pdp-media-story-card');

  const discoverStorySections = () => {
    document.querySelectorAll('.pdp-media-story-card').forEach((story) => {
      if (story.querySelector('.pdp-media-story-card__placeholder')) return;

      const wrapper = story.closest('.shopify-section');
      if (!wrapper || registeredSections.has(wrapper)) return;

      const alreadyInKeyElements = Boolean(wrapper.closest('[data-pdp-key-elements-grid]'));
      let marker = null;
      let homeParent = null;

      if (!alreadyInKeyElements && wrapper.parentNode) {
        marker = document.createComment(`cortailor-pdp-story-home:${wrapper.id || 'story'}`);
        homeParent = wrapper.parentNode;
        wrapper.parentNode.insertBefore(marker, wrapper);
      }

      registeredSections.set(wrapper, { marker, homeParent });
    });
  };

  const getSlides = (grid) => Array.from(grid?.children || []).filter((item) =>
    item.classList?.contains('pdp-key-elements__item') &&
    item.querySelector('.pdp-media-story-card--key-element')
  );

  const ensureTarget = () => {
    const productInfo = getProductInfo();
    if (!productInfo) return null;

    let shell = productInfo.querySelector(':scope > [data-pdp-key-elements]');

    if (!shell) {
      shell = document.createElement('section');
      shell.className = 'pdp-key-elements';
      shell.setAttribute('data-pdp-key-elements', '');
      shell.setAttribute('aria-labelledby', 'pdp-key-elements-heading');
      shell.innerHTML = `
        <h2 class="pdp-key-elements__heading" id="pdp-key-elements-heading">Key Elements</h2>
        <div class="pdp-key-elements__viewport">
          <div class="pdp-key-elements__grid" data-pdp-key-elements-grid></div>
        </div>
        <div class="pdp-key-elements__indicators" data-pdp-key-elements-indicators aria-label="Key Elements carousel position"></div>
      `;
      productInfo.appendChild(shell);
    }

    const moreInfo = productInfo.querySelector(':scope > [data-pdp-more-info-relocate]');
    if (moreInfo && shell.nextElementSibling !== moreInfo) {
      productInfo.insertBefore(shell, moreInfo);
    }

    const grid = shell.querySelector('[data-pdp-key-elements-grid]');
    const indicators = shell.querySelector('[data-pdp-key-elements-indicators]');
    return grid && indicators ? { productInfo, shell, grid, indicators } : null;
  };

  const pruneDisconnected = () => {
    registeredSections.forEach((home, wrapper) => {
      if (wrapper.isConnected) return;
      home.marker?.remove();
      registeredSections.delete(wrapper);
    });
  };


  const restoreStoriesHome = () => {
    registeredSections.forEach((home, wrapper) => {
      if (!wrapper.isConnected) return;

      const story = getStorySection(wrapper);
      const markerParent = home.marker?.parentNode;

      if (markerParent) {
        markerParent.insertBefore(wrapper, home.marker.nextSibling);
      } else if (home.homeParent?.isConnected) {
        home.homeParent.appendChild(wrapper);
      }

      wrapper.classList.remove('pdp-key-elements__item');
      story?.classList.remove('pdp-media-story-card--key-element');
    });
  };

  const removeExistingTarget = () => {
    document.querySelectorAll('[data-pdp-key-elements]').forEach((shell) => shell.remove());
  };

  const moveStoriesIntoTarget = (target) => {
    registeredSections.forEach((home, wrapper) => {
      if (!wrapper.isConnected) return;

      const story = getStorySection(wrapper);
      if (!story) return;

      if (wrapper.parentNode !== target.grid) {
        target.grid.appendChild(wrapper);
      }

      wrapper.classList.add('pdp-key-elements__item');
      story.classList.add('pdp-media-story-card--key-element');
    });
  };

  const setActiveIndicator = (target, activeIndex) => {
    const buttons = Array.from(target.indicators.querySelectorAll('.pdp-key-elements__indicator'));
    if (!buttons.length) return;

    const safeIndex = Math.max(0, Math.min(activeIndex, buttons.length - 1));
    buttons.forEach((button, index) => {
      const active = index === safeIndex;
      button.classList.toggle('is-active', active);
      if (active) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  };

  const getSlideLeft = (grid, slide) => {
    const gridRect = grid.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    return slideRect.left - gridRect.left + grid.scrollLeft;
  };

  const updateActiveFromScroll = (target) => {
    if (desktopQuery.matches) return;

    const slides = getSlides(target.grid);
    if (!slides.length) return;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const distance = Math.abs(getSlideLeft(target.grid, slide) - target.grid.scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndicator(target, closestIndex);
  };

  const bindScrollTracking = (target) => {
    if (target.grid.dataset.pdpKeyElementsCarouselBound === 'true') return;
    target.grid.dataset.pdpKeyElementsCarouselBound = 'true';

    let scrollRaf = 0;
    target.grid.addEventListener('scroll', () => {
      if (desktopQuery.matches || scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = 0;
        updateActiveFromScroll(target);
      });
    }, { passive: true });
  };

  const syncIndicators = (target) => {
    // Key Elements is an expanded editorial list at every breakpoint.
    // Keep the former carousel controls out of the accessibility tree.
    target.indicators.replaceChildren();
    target.indicators.hidden = true;
    target.grid.scrollLeft = 0;
  };

  const refresh = () => {
    refreshRaf = 0;
    discoverStorySections();
    pruneDisconnected();

    if (desktopQuery.matches) {
      restoreStoriesHome();
      removeExistingTarget();
      return;
    }

    const target = ensureTarget();
    if (!target) return;

    moveStoriesIntoTarget(target);

    const slides = getSlides(target.grid);
    target.shell.hidden = slides.length === 0;
    syncIndicators(target);
  };

  const requestRefresh = () => {
    if (refreshRaf) return;
    refreshRaf = window.requestAnimationFrame(refresh);
  };

  window[GLOBAL_KEY] = { refresh: requestRefresh };

  desktopQuery.addEventListener?.('change', requestRefresh);
  window.addEventListener('resize', requestRefresh, { passive: true });
  window.addEventListener('load', requestRefresh, { passive: true });
  document.addEventListener('shopify:section:load', requestRefresh);
  document.addEventListener('shopify:section:reorder', requestRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', requestRefresh, { once: true });
  } else {
    requestRefresh();
  }

  window.setTimeout(requestRefresh, 120);
  window.setTimeout(requestRefresh, 500);
})();
